import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpRequest } from '@angular/common/http';

import { RegistrationService } from '../../core/services';

import { AbbyService } from './abby.service';

import { AbbyResponse } from './abby-response.model';
import { Document } from './document.model';

import { environment } from '@env/environment';
import { Observable } from 'rxjs';

export const baseUrlBCOS = environment.BASE_URL_BCOS;

@Injectable({
  providedIn: 'root'
})
export class DocumentsService {
  // TODO Volodymyr Logvinov check why we need this variable
  savedFiles: Array<FakeFile> = [];

  rawCollection: Map<string, Array<Document>>;

  webCamAvailable = true;

  private xhr: XMLHttpRequest;

  constructor(private abby: AbbyService,
              private registrationService: RegistrationService,
              private http: HttpClient) {
    this.xhr = new XMLHttpRequest();

    this.rawCollection = new Map();
  }

  getDocumentInfoForParty(partyId) {
    const documents = this.rawCollection.get(partyId);

    if (Array.isArray(documents)) {
      const document = documents.find((doc: Document) => doc.type === 'P' || doc.type === 'I');

      if (document) {
        return {
          number: document.number,
          expireAt: document.expireAt
        };
      }
    } else {
      return {
        number: null,
        expireAt: null
      };
    }
  }

  getPartyDocuments(registrationId, partyId) {
    return this.http.get(`${baseUrlBCOS}/documents/party/${registrationId}/${partyId}`);
  }

  getAdditionalModuleDocuments(registrationId: string, additionalModuleId: string) {
    return this.http.get(`${baseUrlBCOS}/documents/registration/${registrationId}?additionalModuleId=${additionalModuleId}`);
  }

  updateDocumentInfoForParty(partyId, documentInfo: {name?: string, number?: string, expireAt?: string}) {
    let documents = this.rawCollection.get(partyId);

    if (documents) {
      documents = documents.map((doc) => {
        doc.update(documentInfo);

        return doc;
      });
    }

    this.rawCollection.set(partyId, documents);
  }

  scanDocument(partyId: string): Promise<AbbyResponse | null> {
    const documents = this.rawCollection.get(partyId) || [];
    const files = documents.map(({file}) => file);

    // TODO commented before we make better solution with keeping scan results
    // if (this.compareDocuments(files, this.savedFiles)) {
    //   return Promise.resolve(null);
    // }

    this.savedFiles = files.map(({name, type, size}) => ({name, type, size}));

    const fileToScan: any = (documents[0].type === 'P') ? files[0] : files[1];

    return this.abby.processMRZ(fileToScan)
      .then(({response}) => {
        const taskId = response.task[0]['$'].id;

        return this.abby.getTaskResult(taskId);
      })
      .then((response?: AbbyResponse) => {
        // eslint-disable-next-line no-prototype-builtins
        if (response.hasOwnProperty('Line1')
            && response.BirthDateVerified === 'true'
            && response.ExpiryDateVerified === 'true') {
          const abbyResponse = new AbbyResponse(response);
          this.updateDocumentInfoForParty(partyId, {number: abbyResponse.number, expireAt: abbyResponse.expireAt});

          return abbyResponse;
        }

        return null;
      });
  }

  compareDocuments(files: Array<File | Document>, savedFiles: Array<FakeFile>) {
    if (!savedFiles) return false;
    if (files.length !== savedFiles.length) return false;

    return files.every((file: File, idx: number) =>
      (file.name === savedFiles[idx].name) &&
      (file.type === savedFiles[idx].type) &&
      (file.size === savedFiles[idx].size)
    );
  }

  base64ToFile(base64: string, filename: string) {
    const byteString = atob(base64.split(',')[1]);
    const mimeString = base64.split(',')[0].split(':')[1].split(';')[0];

    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }

    const blob = new Blob([ab], { type: mimeString });
    blob['lastModifiedDate'] = (new Date()).toISOString();
    blob['name'] = filename;

    return <File> blob;
  }

  async uploadDocuments(tenantId: string, registrationId: string, partyId: string) {
    let documents = this.rawCollection.get(partyId);

    documents = await this.prepareLinks(documents);

    for (const document of documents) {
      await this.uploadFile(document.file, document.url);
    }

    const rawDocuments = await this.postUploadConnect(documents);
    const partyDocuments = [...<Array<Document>> rawDocuments];

    this.rawCollection.set(partyId, null);

    return partyDocuments;
  }

  removeDocument(documentId: string) {
    const tenantId = this.registrationService.tenantId;
    const registrationId = this.registrationService.root._id;

    return this.http.delete(`${baseUrlBCOS}/documents/${tenantId}/${registrationId}/${documentId}`);
  }

  // TODO remove this function
  removeDocumentPromise(tenantId: string, registrationId: string, documentId: string) {
    return this.removeDocument(documentId).toPromise();
  }

  preUploadUrl(fileName: string, mimeType: string, partyId?: string): Observable<any> {
    const tenantId = this.registrationService.tenantId;
    const registrationId = this.registrationService.root._id;

    const args: any = {
      fileName,
      mimeType,
    };

    if (partyId) {
      args.partyId = partyId;
    }

    const params = new HttpParams({fromObject: args});

    return this.http.get(`${baseUrlBCOS}/documents/${tenantId}/${registrationId}/preUpload`, {params});
  }

  getDocument(documentId): Observable<Document> {
    const tenantId = this.registrationService.tenantId;
    const registrationId = this.registrationService.root._id;

    return this.http.get<Document>(`${baseUrlBCOS}/documents/${tenantId}/${registrationId}/${documentId}`);
  }

  postUpload(document: Document, transaction?: string) {
    const tenantId = this.registrationService.tenantId;
    const registrationId = this.registrationService.root._id;

    const transactionParam = (transaction ? `?transaction=${transaction}` : '');

    return this.http.post(`${baseUrlBCOS}/documents/${tenantId}/${registrationId}/postUpload${transactionParam}`, document);
  }

  postUploadArray(documents: Array<Document>) {
    const tenantId = this.registrationService.tenantId;
    const registrationId = this.registrationService.root._id;

    return this.http.post(`${baseUrlBCOS}/documents/${tenantId}/${registrationId}/postUpload?array=true`, documents);
  }

  uploadFileWithProgressReport(signedUploadUrl: string, file: File): Observable<any> {
    const request = new HttpRequest('PUT', signedUploadUrl, file, {reportProgress: true});

    return this.http.request(request);
  }

  // todo Volodymy Logvinov replace it with uploadFileWithProgressReport
  private uploadFile(file: File | undefined, uploadUrl: string) {
    return new Promise((resolve, reject) => {
      this.xhr.open('PUT', uploadUrl, true);

      this.xhr.setRequestHeader('Content-Type', file.type);
      this.xhr.onerror = (err) => {
        reject(err);
      };

      this.xhr.onload = () => {
        const successStatus = 200, successStatusMax = 299;
        if (this.xhr.status >= successStatus && this.xhr.status <= successStatusMax) {
          resolve(uploadUrl);
        }
      };

      this.xhr.send(file);
    });
  }

  private prepareLinks(documents: Array<Document>): Promise<Array<Document>> {
    const promiseList = documents.map((document: Document) => {
      return this.preUploadUrl(document.fileName.normalize(), document.file.type, document.partyId).toPromise();
    });

    return Promise.all(promiseList)
      .then((res: Array<{_id: string, signedUploadUrl: string}>) => {
        return documents.map((document: Document, idx: number) => {
          document._id = res[idx]._id;
          document.url = res[idx].signedUploadUrl;

          return document;
        });
      });
  }

  private postUploadConnect(documents: Array<Document>) {
    const parsedDocuments = documents.map((document: Document) => {
      return {
        _id: document._id,
        partyId: document.partyId,
        fileName: document.fileName,
        type: document.type,
        name: document.name,
        number: document.number,
        expireAt: document.expireAt
      };
    });

    return this.postUploadArray(<Array<Document>> parsedDocuments).toPromise();
  }
}
